import { useEffect, useState } from "react";
import {
  Calendar,
  CheckCircle2,
  FileText,
  Loader2,
  Package,
  Thermometer,
  Truck,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { fetchAdminOrder, formatOrderNumber } from "@/lib/ordersApi";
import type { OrderDetail } from "@/lib/types";
import type { AdminDeliveryRoute } from "@/lib/adminDeliveryApi";
import type { DeliveredOrder } from "@/lib/driverApi";
import { formatDeliveredAt, formatPtDate } from "@/lib/deliveryDate";
import { formatQuantity } from "@/lib/number";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string | null;
  source: "route" | "delivered";
  routeSummary?: AdminDeliveryRoute | null;
  deliveredSummary?: DeliveredOrder | null;
}

export function DeliveryDetailDialog({
  open,
  onOpenChange,
  orderId,
  source,
  routeSummary,
  deliveredSummary,
}: Props) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !orderId) {
      setOrder(null);
      setError(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    fetchAdminOrder(orderId)
      .then((detail) => {
        if (alive) setOrder(detail);
      })
      .catch(() => {
        if (alive) setError("Não foi possível carregar os detalhes da entrega.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, orderId]);

  const title = source === "delivered" ? "Entrega concluída" : "Em rota";
  const clientName = order?.clientName ?? routeSummary?.clientName ?? deliveredSummary?.clientName ?? "Cliente";
  const numberLabel = order?.number != null ? formatOrderNumber(order.number) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            {source === "delivered" ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            ) : (
              <Truck className="h-5 w-5 text-primary" />
            )}
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="-mx-6 flex-1 space-y-4 overflow-y-auto px-6">
          {/* Header card */}
          <div className="rounded-xl border border-border bg-secondary/30 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-display text-lg font-semibold">{clientName}</p>
                {numberLabel && (
                  <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">{numberLabel}</p>
                )}
              </div>
              {deliveredSummary?.nfNumber && (
                <Badge className="border-0 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300">
                  NF {deliveredSummary.nfNumber}
                </Badge>
              )}
            </div>
          </div>

          {loading && (
            <div className="rounded-xl border border-border bg-secondary/20 p-8 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
              Carregando detalhes...
            </div>
          )}

          {error && !loading && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Route info (Em rota) */}
          {source === "route" && routeSummary && (
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailField
                icon={Calendar}
                label="Janela de entrega"
                value={routeSummary.deliveryWindow || formatPtDate(routeSummary.deliveryDate ?? null)}
              />
              <DetailField icon={User} label="Motorista" value={routeSummary.driverName ?? "Não atribuído"} />
              {routeSummary.routeStartedAt && (
                <DetailField
                  icon={Truck}
                  label="Saída"
                  value={formatDeliveredAt(routeSummary.routeStartedAt)}
                />
              )}
              {routeSummary.notes && (
                <DetailField icon={FileText} label="Observações" value={routeSummary.notes} fullWidth />
              )}
            </div>
          )}

          {/* Delivery confirmation (Entregues) */}
          {source === "delivered" && deliveredSummary && (
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailField icon={User} label="Motorista" value={deliveredSummary.driverName} />
              <DetailField
                icon={Calendar}
                label="Entregue às"
                value={formatDeliveredAt(deliveredSummary.deliveredAt)}
              />
              <DetailField
                icon={Thermometer}
                label="Temperatura"
                value={`${deliveredSummary.truckTemperature} °C`}
              />
              <DetailField icon={User} label="Conferente" value={deliveredSummary.receiverName} />
              <DetailField
                icon={FileText}
                label="Documento do conferente"
                value={deliveredSummary.receiverDocument}
              />
            </div>
          )}

          {/* Photos */}
          {source === "delivered" && deliveredSummary && (
            <div className="grid gap-3 sm:grid-cols-2">
              {deliveredSummary.signatureUrl && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Assinatura
                  </p>
                  <div className="overflow-hidden rounded-xl border border-border bg-surface">
                    <img
                      src={deliveredSummary.signatureUrl}
                      alt="Assinatura do conferente"
                      className="h-40 w-full object-contain"
                    />
                  </div>
                </div>
              )}
              {deliveredSummary.receiptPhotoUrl && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Comprovante
                  </p>
                  <div className="overflow-hidden rounded-xl border border-border bg-surface">
                    <img
                      src={deliveredSummary.receiptPhotoUrl}
                      alt="Foto do comprovante"
                      className="h-40 w-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Order items */}
          {order && order.items.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Itens do pedido ({order.items.length})
              </p>
              <ul className="space-y-1.5">
                {order.items.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                  >
                    <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{item.productName}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatQuantity(item.quantity)} {item.unit}
                        {item.cut ? ` · ${item.cut}` : ""}
                      </p>
                      {item.observation && (
                        <p className="mt-0.5 text-xs text-amber-700">Obs.: {item.observation}</p>
                      )}
                    </div>
                    {item.pricePerUnit != null && (
                      <span className="text-xs tabular-nums text-muted-foreground">
                        R$ {(item.pricePerUnit * item.quantity).toFixed(2)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface DetailFieldProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | null | undefined;
  fullWidth?: boolean;
}

function DetailField({ icon: Icon, label, value, fullWidth }: DetailFieldProps) {
  return (
    <div className={`rounded-xl border border-border bg-secondary/20 p-3 ${fullWidth ? "sm:col-span-2" : ""}`}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-1 break-words text-sm font-medium">{value || "—"}</p>
    </div>
  );
}
